import { useId, useState } from 'react'
import type { AdminBalanceProvider, AdminBalanceSeriesPoint } from '@/shared/api/types'
import { formatAdminDate, formatDay, formatNumber } from '../shared/format'
import { formatUsd } from '../shared/adminCosts'
import {
  chartTooltipPlacement,
  focusableChartIndexes,
  providerChartTone,
  providerDisplayName,
  timeChartLabelIndexes,
  timeChartPositionShares,
  type ChartTooltipAlignment,
} from './balanceModel'
import styles from './AdminBalancePage.module.css'

interface ChartTooltip {
  key: string
  left: number
  top: number
  text: string
  align: ChartTooltipAlignment
  maxWidthPercent: number
}

export function BalanceHistoryCharts({ providers }: { providers: AdminBalanceProvider[] }) {
  const withHistory = providers.filter(
    (provider) => Array.isArray(provider.series) && provider.series.length > 0,
  )

  return (
    <section className={styles.chartPanel} aria-labelledby="balance-history-title">
      <div className={styles.chartPanelHeader}>
        <div>
          <h2 id="balance-history-title" className={styles.sectionTitle}>
            Остаток по провайдерам
          </h2>
          <p className={styles.chartHint}>Каждая линия показана в единицах своего провайдера.</p>
        </div>
      </div>

      {withHistory.length === 0 ? (
        <div className={styles.chartEmpty}>Истории баланса за этот период пока нет.</div>
      ) : (
        <div className={styles.lineCharts}>
          {withHistory.map((provider) => (
            <ProviderLineChart key={provider.provider} provider={provider} />
          ))}
        </div>
      )}
    </section>
  )
}

function ProviderLineChart({ provider }: { provider: AdminBalanceProvider }) {
  const tooltipId = useId()
  const [tooltip, setTooltip] = useState<ChartTooltip | null>(null)
  const points = provider.series.filter((point) => Number.isFinite(point.balance))
  const chartTone = providerChartTone(provider.provider)

  if (points.length === 0) {
    return (
      <article className={styles.lineChartCard}>
        <h3 className={styles.chartSubtitle}>{providerDisplayName(provider.provider)}</h3>
        <div className={styles.chartEmpty}>Провайдер не вернул корректную историю.</div>
      </article>
    )
  }

  const width = 720
  const height = 220
  const padX = 30
  const padY = 26
  const values = points.map((point) => point.balance)
  const min = Math.min(...values)
  const max = Math.max(...values)
  const range = Math.max(max - min, Math.abs(max) * 0.08, 1)
  const floor = min - range * 0.08
  const ceiling = max + range * 0.08
  const xShares = timeChartPositionShares(points.map((point) => point.takenAt))
  const labelIndexes = timeChartLabelIndexes(points.map((point) => point.takenAt))
  const x = (index: number) => padX + (xShares[index] ?? 0.5) * (width - padX * 2)
  const y = (value: number) =>
    height - padY - ((value - floor) / Math.max(1, ceiling - floor)) * (height - padY * 2)
  const line = points.map((point, index) => `${x(index)},${y(point.balance)}`).join(' ')
  const focusablePoints = new Set(focusableChartIndexes(points.length))

  function showTooltip(point: AdminBalanceSeriesPoint, index: number) {
    const left = (x(index) / width) * 100
    setTooltip({
      key: `${point.takenAt}-${index}`,
      left,
      top: (y(point.balance) / height) * 100,
      text: `${formatAdminDate(point.takenAt)} · ${formatProviderBalance(provider.provider, point.balance)}`,
      ...chartTooltipPlacement(left),
    })
  }

  return (
    <article className={styles.lineChartCard}>
      <div className={styles.lineChartHeader}>
        <h3 className={styles.chartSubtitle}>{providerDisplayName(provider.provider)}</h3>
        {points.length !== provider.series.length && (
          <span className={styles.dataWarning}>Часть точек повреждена</span>
        )}
      </div>
      <div className={styles.svgFrame} onMouseLeave={() => setTooltip(null)}>
        <svg
          className={styles.chart}
          viewBox={`0 0 ${width} ${height}`}
          role="group"
          aria-label={`История остатка ${providerDisplayName(provider.provider)}`}
        >
          <title>История остатка {providerDisplayName(provider.provider)}</title>
          {[0.25, 0.5, 0.75].map((share) => (
            <line
              key={share}
              className={styles.gridLine}
              x1={padX}
              x2={width - padX}
              y1={padY + (height - padY * 2) * share}
              y2={padY + (height - padY * 2) * share}
            />
          ))}
          <polyline
            className={chartTone === 'primary' ? styles.balanceLine : styles.balanceLineSecondary}
            points={line}
          />
          {points.map((point, index) => {
            const pointKey = `${point.takenAt}-${index}`
            const label = `${formatAdminDate(point.takenAt)}: ${formatProviderBalance(provider.provider, point.balance)}`
            return (
              <circle
                key={pointKey}
                className={
                  chartTone === 'primary' ? styles.chartPoint : styles.chartPointSecondary
                }
                cx={x(index)}
                cy={y(point.balance)}
                r="5"
                tabIndex={focusablePoints.has(index) ? 0 : -1}
                data-chart-point="balance"
                aria-label={label}
                aria-describedby={tooltip?.key === pointKey ? tooltipId : undefined}
                onMouseEnter={() => showTooltip(point, index)}
                onFocus={() => showTooltip(point, index)}
                onBlur={() => setTooltip(null)}
              >
                <title>{label}</title>
              </circle>
            )
          })}
        </svg>
        {tooltip && (
          <div
            id={tooltipId}
            key={tooltip.key}
            className={`${styles.chartTooltip} ${
              tooltip.align === 'start'
                ? styles.chartTooltipStart
                : tooltip.align === 'end'
                  ? styles.chartTooltipEnd
                  : ''
            }`}
            role="tooltip"
            style={{
              left: `${tooltip.left}%`,
              top: `${tooltip.top}%`,
              maxWidth: `min(260px, ${tooltip.maxWidthPercent}%)`,
            }}
          >
            {tooltip.text}
          </div>
        )}
      </div>
      <div className={styles.chartLabels}>
        {labelIndexes.map((index) => {
          const point = points[index]
          return point ? (
            <span key={`${point.takenAt}-${index}`}>
              {formatDay(point.takenAt.slice(0, 10))}
            </span>
          ) : null
        })}
      </div>
      <ChartDataTable
        caption={`Все значения остатка ${providerDisplayName(provider.provider)}`}
        rows={points.map((point, index) => ({
          key: `${point.takenAt}-${index}`,
          date: formatChartTimestamp(point.takenAt),
          dateTime: point.takenAt,
          provider: providerDisplayName(provider.provider),
          value: formatProviderBalance(provider.provider, point.balance),
        }))}
      />
    </article>
  )
}

export function DailySpendChart({ providers }: { providers: AdminBalanceProvider[] }) {
  const tooltipId = useId()
  const [tooltip, setTooltip] = useState<ChartTooltip | null>(null)
  const groups = dailySpendGroups(providers)

  if (groups.length === 0) {
    return (
      <section className={styles.chartPanel} aria-labelledby="daily-spend-title">
        <h2 id="daily-spend-title" className={styles.sectionTitle}>
          Расход по дням
        </h2>
        <div className={styles.chartEmpty}>Расходов за этот период пока нет.</div>
      </section>
    )
  }

  const width = 900
  const height = 250
  const padX = 34
  const padY = 28
  const plotWidth = width - padX * 2
  const plotHeight = height - padY * 2
  const max = Math.max(0.01, ...groups.map((group) => group.total))
  const slot = plotWidth / groups.length
  const barWidth = Math.max(2, Math.min(24, slot * 0.72))
  const labels = chartLabels(groups)
  const providersInChart = Array.from(
    new Set(groups.flatMap((group) => group.items.map((item) => item.provider))),
  )
  const barOffsets: number[] = []
  let barCount = 0
  for (const group of groups) {
    barOffsets.push(barCount)
    barCount += group.items.length
  }
  const focusableBars = new Set(focusableChartIndexes(barCount))
  const spendDataRows = groups.flatMap((group, groupIndex) =>
    group.items.map((item, itemIndex) => ({
      key: `${group.date}-${item.provider}-${groupIndex}-${itemIndex}`,
      date: formatDay(group.date),
      dateTime: group.date,
      provider: providerDisplayName(item.provider),
      value: formatUsd(item.value),
    })),
  )

  return (
    <section className={styles.chartPanel} aria-labelledby="daily-spend-title">
      <div className={styles.chartPanelHeader}>
        <div>
          <h2 id="daily-spend-title" className={styles.sectionTitle}>
            Расход по дням
          </h2>
          <p className={styles.chartHint}>Сумма списаний провайдеров в долларах США.</p>
        </div>
        <div className={styles.chartLegend} aria-label="Провайдеры на графике">
          {providersInChart.map((provider) => (
            <span key={provider}>
              <i
                className={
                  providerChartTone(provider) === 'primary'
                    ? styles.legendPrimary
                    : styles.legendSecondary
                }
              />
              {providerDisplayName(provider)}
            </span>
          ))}
        </div>
      </div>

      <div className={styles.svgFrame} onMouseLeave={() => setTooltip(null)}>
        <svg
          className={styles.chart}
          viewBox={`0 0 ${width} ${height}`}
          role="group"
          aria-label="Ежедневные расходы OpenRouter и ElevenLabs"
        >
          <title>Ежедневные расходы OpenRouter и ElevenLabs</title>
          {[0.25, 0.5, 0.75, 1].map((share) => (
            <line
              key={share}
              className={styles.gridLine}
              x1={padX}
              x2={width - padX}
              y1={height - padY - plotHeight * share}
              y2={height - padY - plotHeight * share}
            />
          ))}
          {groups.map((group, groupIndex) => {
            let accumulated = 0
            const left = padX + groupIndex * slot + (slot - barWidth) / 2
            return group.items.map((item, itemIndex) => {
              const barIndex = (barOffsets[groupIndex] ?? 0) + itemIndex
              const valueHeight = (item.value / max) * plotHeight
              const top = height - padY - accumulated - valueHeight
              accumulated += valueHeight
              const label = `${formatDay(group.date)} · ${item.label || providerDisplayName(item.provider)}: ${formatUsd(item.value)}`
              const leftPercent = ((left + barWidth / 2) / width) * 100
              const tooltipData: ChartTooltip = {
                key: `${group.date}-${item.provider}-${itemIndex}`,
                left: leftPercent,
                top: (top / height) * 100,
                text: label,
                ...chartTooltipPlacement(leftPercent),
              }
              return (
                <rect
                  key={tooltipData.key}
                  className={
                    providerChartTone(item.provider) === 'primary'
                      ? styles.spendBar
                      : styles.spendBarSecondary
                  }
                  x={left}
                  y={top}
                  width={barWidth}
                  height={Math.max(1, valueHeight)}
                  rx="2"
                  tabIndex={focusableBars.has(barIndex) ? 0 : -1}
                  data-chart-point="spend"
                  aria-label={label}
                  aria-describedby={tooltip?.key === tooltipData.key ? tooltipId : undefined}
                  onMouseEnter={() => setTooltip(tooltipData)}
                  onFocus={() => setTooltip(tooltipData)}
                  onBlur={() => setTooltip(null)}
                >
                  <title>{label}</title>
                </rect>
              )
            })
          })}
        </svg>
        {tooltip && (
          <div
            id={tooltipId}
            key={tooltip.key}
            className={`${styles.chartTooltip} ${
              tooltip.align === 'start'
                ? styles.chartTooltipStart
                : tooltip.align === 'end'
                  ? styles.chartTooltipEnd
                  : ''
            }`}
            role="tooltip"
            style={{
              left: `${tooltip.left}%`,
              top: `${tooltip.top}%`,
              maxWidth: `min(260px, ${tooltip.maxWidthPercent}%)`,
            }}
          >
            {tooltip.text}
          </div>
        )}
      </div>
      <div className={styles.chartLabels}>
        {labels.map((group) => (
          <span key={group.date}>{formatDay(group.date)}</span>
        ))}
      </div>
      <ChartDataTable caption="Все значения расходов по дням" rows={spendDataRows} />
    </section>
  )
}

interface ChartDataRow {
  key: string
  date: string
  dateTime: string
  provider: string
  value: string
}

function ChartDataTable({ caption, rows }: { caption: string; rows: ChartDataRow[] }) {
  return (
    <details className={styles.chartData}>
      <summary>Таблица данных</summary>
      <div className={styles.chartDataTableWrap}>
        <table className={styles.chartDataTable}>
          <caption>{caption}</caption>
          <thead>
            <tr>
              <th scope="col">Дата</th>
              <th scope="col">Провайдер</th>
              <th scope="col">Значение</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                <td>
                  <time dateTime={row.dateTime}>{row.date}</time>
                </td>
                <td>{row.provider}</td>
                <td>{row.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  )
}

function formatChartTimestamp(value: string): string {
  const formatted = formatAdminDate(value)
  return formatted === '—' ? value || '—' : formatted
}

function formatProviderBalance(provider: string, value: number): string {
  return provider.toLowerCase() === 'elevenlabs'
    ? `${formatNumber(value)} кр.`
    : formatUsd(value)
}

function chartLabels<T>(items: T[]): T[] {
  if (items.length <= 3) return items
  const indexes = [0, Math.floor(items.length / 2), items.length - 1]
  return indexes
    .map((index) => items[index])
    .filter((item): item is T => item !== undefined)
}

interface DailySpendGroup {
  date: string
  total: number
  items: {
    provider: string
    value: number
    label: string
  }[]
}

function dailySpendGroups(providers: AdminBalanceProvider[]): DailySpendGroup[] {
  const groups = new Map<string, DailySpendGroup>()

  for (const provider of providers) {
    if (!Array.isArray(provider.dailySpend)) continue
    for (const point of provider.dailySpend) {
      if (!Number.isFinite(point.value)) continue
      const group = groups.get(point.date) ?? { date: point.date, total: 0, items: [] }
      group.items.push({
        provider: provider.provider,
        value: point.value,
        label: point.label,
      })
      group.total += point.value
      groups.set(point.date, group)
    }
  }

  return [...groups.values()].sort((left, right) => left.date.localeCompare(right.date))
}
